import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Paper,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  Fade,
  Zoom,
  Collapse,
  Badge,
  LinearProgress,
  Switch,
  FormControlLabel,
  Slider,
  useTheme,
  alpha,
  GlobalStyles
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ContentCopy as ContentCopyIcon,
  Lock as LockIcon,
  MoreVert as MoreVertIcon,
  FileDownload as FileDownloadIcon,
  Share as ShareIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  FormatSize as FontSizeIcon,
  Lightbulb as LightbulbIcon,
  Public as PublicIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { formatEpochDateOnly, getRelativeTimeFromEpochDaysOnly } from '../../utils/dateUtils';

// Removed global animations

// Enhanced styled components
const ViewerContainer = styled(Box)(({ theme, fullscreen }) => ({
  position: fullscreen ? 'fixed' : 'relative',
  top: fullscreen ? 0 : 'auto',
  left: fullscreen ? 0 : 'auto',
  right: fullscreen ? 0 : 'auto',
  bottom: fullscreen ? 0 : 'auto',
  zIndex: fullscreen ? 9999 : 1,
  width: fullscreen ? '100vw' : 'auto',
  height: fullscreen ? '100vh' : 'auto',
  background: fullscreen 
    ? `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)}, ${alpha(theme.palette.background.paper, 0.95)})`
    : 'transparent',
  backdropFilter: fullscreen ? 'blur(20px)' : 'none',
  overflow: fullscreen ? 'auto' : 'visible',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: fullscreen ? theme.spacing(2) : 0,
}));

const HeroSection = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: 'white',
  borderRadius: 32,
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.4)}`,
}));

const GlassCard = styled(Card)(({ theme }) => ({
  borderRadius: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.15)}`,
  overflow: 'hidden',
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  borderRadius: 0,
  overflow: 'hidden',
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.12)}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const HeaderBar = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
  backdropFilter: 'blur(10px)',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const ToolbarContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
  backdropFilter: 'blur(10px)',
  padding: theme.spacing(1.5, 3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const StatsCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.9)})`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 20,
}));

const ActionButton = styled(Button)(({ theme, variant: buttonVariant = 'primary' }) => ({
  borderRadius: 16,
  padding: '12px 24px',
  fontWeight: 600,
  textTransform: 'none',
  background: buttonVariant === 'primary'
    ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
    : buttonVariant === 'success'
    ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
    : `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.1)}, ${alpha(theme.palette.text.primary, 0.05)})`,
  color: buttonVariant === 'outline' ? theme.palette.text.primary : 'white',
  border: buttonVariant === 'outline' ? `2px solid ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
  boxShadow: buttonVariant !== 'outline' ? `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
  position: 'relative',
  overflow: 'hidden',
}));

const GlowingIconButton = styled(IconButton)(({ theme, variant = 'default' }) => ({
  borderRadius: 16,
  padding: 12,
  background: variant === 'primary' 
    ? alpha(theme.palette.primary.main, 0.1)
    : variant === 'success'
    ? alpha(theme.palette.success.main, 0.1)
    : variant === 'warning'
    ? alpha(theme.palette.warning.main, 0.1)
    : alpha(theme.palette.action.hover, 0.5),
  color: variant === 'primary' 
    ? theme.palette.primary.main
    : variant === 'success'
    ? theme.palette.success.main
    : variant === 'warning'
    ? theme.palette.warning.main
    : theme.palette.text.primary,
  border: `1px solid ${
    variant === 'primary' 
      ? alpha(theme.palette.primary.main, 0.2)
      : variant === 'success'
      ? alpha(theme.palette.success.main, 0.2)
      : variant === 'warning'
      ? alpha(theme.palette.warning.main, 0.2)
      : alpha(theme.palette.divider, 0.2)
  }`,
}));

const EnhancedChip = styled(Chip)(({ theme, chipcolor }) => ({
  borderRadius: 16,
  fontWeight: 600,
  fontSize: '0.8rem',
  padding: '8px 16px',
  background: chipcolor === 'private' 
    ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)}, ${alpha(theme.palette.warning.dark, 0.15)})`
    : chipcolor === 'syntax'
    ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)}, ${alpha(theme.palette.info.dark, 0.15)})`
    : chipcolor === 'time'
    ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)}, ${alpha(theme.palette.success.dark, 0.15)})`
    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.dark, 0.15)})`,
  color: chipcolor === 'private' 
    ? theme.palette.warning.main 
    : chipcolor === 'syntax'
    ? theme.palette.info.main
    : chipcolor === 'time'
    ? theme.palette.success.main
    : theme.palette.primary.main,
  border: `1px solid ${
    chipcolor === 'private' 
      ? alpha(theme.palette.warning.main, 0.3) 
      : chipcolor === 'syntax'
      ? alpha(theme.palette.info.main, 0.3)
      : chipcolor === 'time'
      ? alpha(theme.palette.success.main, 0.3)
      : alpha(theme.palette.primary.main, 0.3)
  }`,
  boxShadow: `0 4px 12px ${
    chipcolor === 'private' 
      ? alpha(theme.palette.warning.main, 0.2) 
      : chipcolor === 'syntax'
      ? alpha(theme.palette.info.main, 0.2)
      : chipcolor === 'time'
      ? alpha(theme.palette.success.main, 0.2)
      : alpha(theme.palette.primary.main, 0.2)
  }`,
}));

const ViewSnippet = ({ snippetId, onBack }) => {
  const theme = useTheme();
  
  // State management
  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fontSize, setFontSize] = useState(16);
  const [starred, setStarred] = useState(false);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [fontMenuAnchor, setFontMenuAnchor] = useState(null);
  
  // Editor modes
  const [editorMode, setEditorMode] = useState('note'); // 'note', 'code', 'data'
  
  // Content analysis
  const contentStats = useMemo(() => {
    if (!snippet?.content) return { chars: 0, words: 0, lines: 0, paragraphs: 0 };
    
    const content = snippet.content;
    const chars = content.length;
    const words = content.trim().split(/\s+/).length;
    const lines = content.split('\n').length;
    const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim()).length;
    
    return { chars, words, lines, paragraphs };
  }, [snippet?.content]);

  // Fetch snippet data
  useEffect(() => {
    const fetchSnippet = async () => {
      try {
        setLoading(true);
        if (!snippetId || snippetId === 'undefined') {
          throw new Error('Invalid snippet ID');
        }
        
        const data = await api.snippets.getById(snippetId);
        setSnippet(data);
        
        // Set initial view count and like count (could come from API)
        setViewCount(Math.floor(Math.random() * 1000) + 100);
        setLikeCount(Math.floor(Math.random() * 50) + 10);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching snippet:', err);
        if (err.message === 'Invalid snippet ID') {
          setError('Invalid snippet ID. Please check the URL and try again.');
        } else {
          setError('Failed to load snippet. It may have been deleted or expired.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (snippetId && snippetId !== 'undefined') {
      fetchSnippet();
    } else {
      setError('Invalid snippet ID');
      setLoading(false);
    }
  }, [snippetId]);

  // Handlers
  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(snippet?.content || '');
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleLike = () => {
    setLiked(!liked);
    setLikeCount(prev => liked ? prev - 1 : prev + 1);
  };

  const handleStar = () => {
    setStarred(!starred);
  };

  const handleBookmark = () => {
    setBookmarked(!bookmarked);
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: snippet?.title || 'Shared Content',
        text: 'Check out this content!',
        url: window.location.href,
      });
    } catch (err) {
      // Fallback to clipboard
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!snippet) return;
    
    const element = document.createElement('a');
    const file = new Blob([snippet.content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${(snippet.title || 'snippet').replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleModeChange = (mode) => {
    setEditorMode(mode);
  };

  const getFileExtension = (syntax) => {
    const extensions = {
      'javascript': 'js',
      'typescript': 'ts',
      'python': 'py',
      'java': 'java',
      'html': 'html',
      'css': 'css',
      'csharp': 'cs',
      'cpp': 'cpp',
      'go': 'go',
      'ruby': 'rb',
      'php': 'php',
      'sql': 'sql',
      'xml': 'xml',
      'json': 'json',
      'yaml': 'yml',
      'markdown': 'md',
      'bash': 'sh',
      'powershell': 'ps1',
      'log': 'log'
    };
    
    return extensions[syntax] || 'txt';
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        mt: 4, 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading content...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ mt: 4, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
        <GlassCard sx={{ p: 4 }}>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            {error}
          </Alert>
          <ActionButton 
            variant="outline"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            size="large"
          >
            Back
          </ActionButton>
        </GlassCard>
      </Box>
    );
  }

  // No snippet found
  if (!snippet) {
    return (
      <Box sx={{ mt: 4, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
        <GlassCard sx={{ p: 4 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            Content not found
          </Alert>
          <ActionButton 
            variant="outline"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            size="large"
          >
            Back
          </ActionButton>
        </GlassCard>
      </Box>
    );
  }

  // Main content display
  return (
    <ViewerContainer>
        {/* Simple Header - Just Back Button and Author */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 1 }}>
          <IconButton 
            onClick={onBack}
            sx={{ 
              background: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { background: alpha(theme.palette.primary.main, 0.2) }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
              {snippet.createdByName ? 
                snippet.createdByName.split(' ').map(n => n[0]).join('').toUpperCase() : 'A'}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="600">
                {snippet.createdByName || 'Anonymous'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getRelativeTimeFromEpochDaysOnly(snippet.createdAt)}
              </Typography>
            </Box>
          </Box>
        </Box>



        {/* Main Content */}
        <GlassCard>
            {/* Header Bar with Title */}
            <HeaderBar>
              
              <Typography variant="h6" fontWeight="bold" sx={{ ml: 2 }}>
                {snippet.title || 'Untitled Content'}
              </Typography>
              
              <Box sx={{ flexGrow: 1 }} />
              
              {/* Stats in Header */}
              <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                {contentStats.chars.toLocaleString()} chars • {contentStats.words.toLocaleString()} words • {contentStats.lines} lines
              </Typography>
              
              <Stack direction="row" spacing={1}>
                <Tooltip title="Font Settings">
                  <GlowingIconButton onClick={(e) => setFontMenuAnchor(e.currentTarget)}>
                    <FontSizeIcon />
                  </GlowingIconButton>
                </Tooltip>
                
                <Tooltip title="Copy Content">
                  <GlowingIconButton onClick={handleCopyClick}>
                    <ContentCopyIcon />
                  </GlowingIconButton>
                </Tooltip>
                
                <Tooltip title="More Options">
                  <GlowingIconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <MoreVertIcon />
                  </GlowingIconButton>
                </Tooltip>
              </Stack>
            </HeaderBar>

            {/* Title, Summary, Tags under toolbar */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              
              {snippet.summary && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {snippet.summary}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {snippet.tags && snippet.tags.length > 0 ? (
                  snippet.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No tags</Typography>
                )}
                
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  <Chip 
                    icon={snippet.isPrivate ? <LockIcon /> : <PublicIcon />}
                    label={snippet.isPrivate ? 'Private' : 'Public'}
                    size="small"
                    color={snippet.isPrivate ? 'warning' : 'success'}
                    variant="outlined"
                  />
                  {snippet.expiresAt && snippet.expiresAt !== 0 && (
                    <Chip 
                      icon={<ScheduleIcon />}
                      label={`Expires ${getRelativeTimeFromEpochDaysOnly(snippet.expiresAt)}`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Content Display Area */}
            <Box sx={{ display: 'flex' }}>
              {/* Main Content Display */}
              <Box sx={{ flex: 1 }}>
                <ContentContainer sx={{ display: 'flex', flexDirection: 'column' }}>
                  {/* Universal Content Display with Line Numbers */}
                  <Box sx={{ 
                    p: 0,
                    display: 'flex',
                    minHeight: '400px',
                    fontFamily: editorMode === 'code' 
                      ? '"JetBrains Mono", "Fira Code", monospace'
                      : editorMode === 'note'
                      ? 'system-ui, -apple-system, sans-serif'
                      : '"JetBrains Mono", "Fira Code", monospace',
                    fontSize: `${fontSize}px`,
                    lineHeight: editorMode === 'note' ? 1.8 : 1.6,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
                    color: theme.palette.text.primary,
                    borderRadius: 0,
                    overflow: 'visible',
                    position: 'relative',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    boxShadow: `inset 0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
                  }}>
                    {/* Line Numbers */}
                    <Box sx={{
                      width: '60px',
                      background: alpha(theme.palette.background.default, 0.5),
                      borderRight: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      p: 2,
                      pt: 4,
                      fontSize: '0.875rem',
                      color: theme.palette.text.secondary,
                      fontFamily: 'monospace',
                      userSelect: 'none',
                    }}>
                      {(snippet.content || '').split('\n').map((_, index) => (
                        <Box key={index} sx={{ height: `${editorMode === 'note' ? 1.8 : 1.6}em`, textAlign: 'right', pr: 1 }}>
                          {index + 1}
                        </Box>
                      ))}
                    </Box>
                    
                    {/* Content Area */}
                    <Box sx={{
                      flex: 1,
                      p: 4,
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                      '&::selection': {
                        background: alpha(theme.palette.primary.main, 0.2),
                      },
                      '& *::selection': {
                        background: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}>
                      {snippet.content || 'No content available'}
                    </Box>
                  </Box>
                </ContentContainer>
              </Box>
            </Box>
          </GlassCard>

        {/* Performance Alert */}
        {contentStats.chars > 5000 && (
          <Alert 
              severity="info" 
              sx={{ 
                mt: 3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.dark, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
              icon={<LightbulbIcon />}
            >
              This content contains substantial text ({contentStats.chars.toLocaleString()} characters). 
              Performance optimizations are active for the best viewing experience.
            </Alert>
        )}

        {/* Menus */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: 200,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }
          }}
        >
          <MenuItem onClick={handleDownload} sx={{ borderRadius: 2, mx: 1, my: 0.5 }}>
            <ListItemIcon><FileDownloadIcon /></ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleShare} sx={{ borderRadius: 2, mx: 1, my: 0.5 }}>
            <ListItemIcon><ShareIcon /></ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
          <MenuItem sx={{ borderRadius: 2, mx: 1, my: 0.5 }}>
            <ListItemIcon><VisibilityIcon /></ListItemIcon>
            <ListItemText>View Raw</ListItemText>
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem sx={{ borderRadius: 2, mx: 1, my: 0.5 }}>
            <ListItemIcon><SecurityIcon /></ListItemIcon>
            <ListItemText>Report</ListItemText>
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={fontMenuAnchor}
          open={Boolean(fontMenuAnchor)}
          onClose={() => setFontMenuAnchor(null)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: 300,
              p: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Font Size: {fontSize}px
          </Typography>
          <Slider
            value={fontSize}
            onChange={(e, value) => setFontSize(value)}
            min={10}
            max={24}
            step={1}
            marks
            sx={{ mb: 2 }}
          />
        </Menu>

        {/* Success Snackbar */}
        <Snackbar
          open={copySuccess}
          autoHideDuration={2000}
          onClose={() => setCopySuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity="success" 
            variant="filled"
            sx={{ 
              borderRadius: 3,
              boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            Copied to clipboard! ✨
          </Alert>
        </Snackbar>
      </ViewerContainer>
    );
  };
  
  const ViewSnippetComponent = ViewSnippet;
export default ViewSnippetComponent;
